import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Calendar } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import LoadingSpinner from '../common/LoadingSpinner';
import MetricCard from './MetricCard';
import MetricsFilters from './MetricsFilters';
import {
  asPercent,
  useInstrumentMetrics,
  useInstrumentRegistry,
} from '../../hooks/useMetricsApi';

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-4 w-4 text-slate-400" />
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-medium text-slate-900">{value}</p>
    </div>
  </div>
);

// End-of-semester readings. These are not change claims and must not be read as
// progress: "84% would recommend the program" says nothing about whether
// anything improved, so they live in their own section with their own heading.
const PointInTime = ({ readings }) => {
  if (!readings?.length) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold text-slate-900">End-of-semester responses</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        Asked only at the end of the semester, so these describe where students
        finished -- not how far they moved.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {readings.map((reading) => (
          <div
            key={reading.key}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <p className="text-sm text-slate-700">{reading.label}</p>
            {reading.withheldBecause ? (
              <p className="mt-2 text-xs text-slate-400">{reading.withheldBecause}</p>
            ) : (
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {reading.type === 'yesNo'
                  ? asPercent(reading.rate)
                  : `${reading.median} / ${reading.scaleMax}`}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  {reading.type === 'yesNo'
                    ? `${reading.yes} of ${reading.n} students`
                    : `median, ${reading.n} students`}
                </span>
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const DataQuality = ({ dataQuality }) => {
  if (!dataQuality) return null;
  const kinds = Object.entries(dataQuality.anomalies || {});
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h2 className="text-sm font-semibold text-slate-900">
        Record pairing{' '}
        <span className="font-normal text-slate-500">
          ({dataQuality.defects} defect{dataQuality.defects === 1 ? '' : 's'})
        </span>
      </h2>
      {kinds.length ? (
        <ul className="mt-2 space-y-1">
          {kinds.map(([kind, count]) => (
            <li key={kind} className="text-xs text-slate-600">
              <span className="font-medium">{count}</span> {kind.replace(/_/g, ' ')}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Every baseline paired cleanly with an outcome record.
        </p>
      )}
    </section>
  );
};

const InstrumentPanel = () => {
  const { instrumentKey } = useParams();
  const [filters, setFilters] = useState({ comparison: 'from_intake', sequence: '', since: '' });
  const { data, isLoading, error } = useInstrumentMetrics(instrumentKey, filters);
  const { data: registry } = useInstrumentRegistry();

  const definitions = {};
  registry?.instruments
    ?.find((instrument) => instrument.key === instrumentKey)
    ?.items?.forEach((item) => {
      definitions[item.key] = item;
    });

  if (isLoading) {
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTitle>Could not load metrics</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const outcomes = data.metrics.filter((metric) => metric.kind === 'outcome');
  const others = data.metrics.filter((metric) => metric.kind !== 'outcome');

  return (
    <div className="space-y-6 p-4">
      <div>
        <Link
          to="/dashboard/metrics"
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          <ArrowLeft className="h-4 w-4" />
          All instruments
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-slate-900">{data.instrument.label}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {data.instrument.baselineLabel} compared with {data.instrument.outcomeLabel}
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <MetricsFilters filters={filters} onChange={setFilters} />
        <div className="flex gap-6">
          <Stat icon={Calendar} label="Semesters scored" value={data.episodes} />
          <Stat icon={Users} label="Students" value={data.students} />
        </div>
      </div>

      {data.episodes === 0 ? (
        <Alert>
          <AlertTitle>No semesters match these filters</AlertTitle>
          <AlertDescription>
            Nothing is scored, so nothing is reported. Widen the date or semester
            filter.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold text-slate-900">Change over the semester</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Each item is scored over the students for whom it could move. An item
              is reported only if it clears the privacy floor, has enough paired
              records to say anything, and was answered by enough of the cohort.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {outcomes.map((metric) => (
                <MetricCard
                  key={metric.key}
                  metric={metric}
                  definition={definitions[metric.key]}
                />
              ))}
            </div>
          </section>

          {others.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-slate-900">
                Participation and context
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                These describe how a student engaged with the program rather than a
                therapeutic outcome.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {others.map((metric) => (
                  <MetricCard
                    key={metric.key}
                    metric={metric}
                    definition={definitions[metric.key]}
                  />
                ))}
              </div>
            </section>
          )}

          <PointInTime readings={data.pointInTime} />
          <DataQuality dataQuality={data.dataQuality} />
        </>
      )}
    </div>
  );
};

export default InstrumentPanel;
